import { ChangeEvent, ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer,
  FiltersSubmit
} from 'components/Filters'
import { Input } from 'components/Input'
import { Tag } from 'components/v2/Tag'
import { Select } from 'components/v2/Select'
import { statusMessages } from 'features/Carts/components/Status/messages'
import { ICartsFiltersFormValues, ICartsFiltersProps } from 'features/Carts/views/Index/Filters/types'
import { GTCartStatus } from 'gql/types.generated'
import { safeFormatMessage } from 'utils/intl'
import { getQueryString } from 'utils/route'

const messages = defineMessages({
  namePlaceholder: {
    id: 'spanish.carts.filters.name.placeholder',
    defaultMessage: 'E.g: Carrito'
  },
  selectStatus: {
    id: 'spanish.carts.filters.selectStatus',
    defaultMessage: 'Seleccionar estado'
  }
})

const STATUS_LIST = [GTCartStatus.Available, GTCartStatus.Occupied]

export const CartsFilters = ({ filters, getCartsQuery }: ICartsFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const filterCarts = (values: ICartsFiltersFormValues): void => {
    const query = getCartsQuery(values)
    navigate(`/carts?${getQueryString(query)}`)
  }

  const initialValues = {
    name: filters.name || '',
    status: (filters.status as GTCartStatus) || ''
  }

  return (
    <Formik initialValues={initialValues} onSubmit={filterCarts} enableReinitialize>
      {(props): ReactElement => {
        const { handleSubmit, setFieldValue, values } = props

        const handleSelectChange = ({ target: { id, value } }: ChangeEvent<HTMLSelectElement>): void => {
          setFieldValue(id, value)

          filterCarts({
            ...values,
            [id]: value
          })
        }

        const handleFilterDelete = (id: string): void => {
          setFieldValue(id, '')
          filterCarts({
            ...values,
            [id]: ''
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'name':
                          label = <FormattedMessage id="spanish.dictionary.name" defaultMessage="Nombre" />
                          value = filterValue
                          break
                        case 'status':
                          label = <FormattedMessage id="spanish.dictionary.status" defaultMessage="Estado" />
                          value = safeFormatMessage({ intl, messages: statusMessages, key: filterValue.toLowerCase() })
                          break
                      }

                      return (
                        <Tag key={filterKey} onDelete={handleFilterDelete.bind(this, filterKey)}>
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <Input
                    id="name"
                    placeholder={intl.formatMessage(messages.namePlaceholder)}
                    value={values.name}
                    label={<FormattedMessage id="spanish.carts.filters.name.label" defaultMessage="Nombre" />}
                    onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
                      setFieldValue('name', value)

                      filterCarts({
                        ...values,
                        name: value
                      })
                    }}
                  />
                  <Select
                    id="status"
                    value={values.status}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="spanish.dictionary.status" defaultMessage="Estado" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>
                    {STATUS_LIST.map((status): TSFixMe => {
                      return (
                        <option value={status} key={status}>
                          {safeFormatMessage({ intl, messages: statusMessages, key: status.toLowerCase() })}
                        </option>
                      )
                    })}
                  </Select>
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <FiltersSubmit />
          </form>
        )
      }}
    </Formik>
  )
}
